import { Box, Button, Group, Text } from '@mantine/core'
import { useEffect, useRef, useState } from 'react'
import { LuEraser, LuSignature } from 'react-icons/lu'

interface Props {
  /** Initial signature dataURL (e.g. when re-editing). */
  value?: string | null
  /** Called with PNG dataURL when user finishes a stroke. Empty string when cleared. */
  onChange: (dataUrl: string) => void
  width?: number
  height?: number
  label?: string
}

/**
 * Canvas signature pad — mouse + touch. Emits PNG dataURL on stroke end.
 * Designed to be lightweight (no external lib). Strokes drawn with smooth
 * quadratic bezier between sampled points for natural ink feel.
 */
export function SignaturePad({ value, onChange, width = 480, height = 160, label = 'Tanda tangan' }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const drawing = useRef(false)
  const lastPoint = useRef<{ x: number; y: number } | null>(null)
  const [isEmpty, setIsEmpty] = useState(true)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    // Use device pixel ratio for crisp lines on retina.
    const dpr = window.devicePixelRatio || 1
    canvas.width = width * dpr
    canvas.height = height * dpr
    canvas.style.width = `${width}px`
    canvas.style.height = `${height}px`
    ctx.scale(dpr, dpr)
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#1a1a1a'
    ctx.lineWidth = 2
    if (value) {
      const img = new Image()
      img.onload = () => {
        ctx.drawImage(img, 0, 0, width, height)
        setIsEmpty(false)
      }
      img.src = value
    }
  }, [value, width, height])

  function pointFrom(e: PointerEvent | React.PointerEvent): { x: number; y: number } {
    const canvas = canvasRef.current!
    const rect = canvas.getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    e.preventDefault()
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.setPointerCapture(e.pointerId)
    drawing.current = true
    const p = pointFrom(e)
    lastPoint.current = p
    const ctx = canvas.getContext('2d')!
    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
    ctx.lineTo(p.x + 0.01, p.y + 0.01)
    ctx.stroke()
    setIsEmpty(false)
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return
    const ctx = canvasRef.current!.getContext('2d')!
    const p = pointFrom(e)
    const last = lastPoint.current!
    const mid = { x: (last.x + p.x) / 2, y: (last.y + p.y) / 2 }
    ctx.quadraticCurveTo(last.x, last.y, mid.x, mid.y)
    ctx.stroke()
    lastPoint.current = p
  }

  function onPointerUp() {
    if (!drawing.current) return
    drawing.current = false
    const canvas = canvasRef.current!
    onChange(canvas.toDataURL('image/png'))
  }

  function clear() {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')!
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setIsEmpty(true)
    onChange('')
  }

  return (
    <Box>
      <Group justify="space-between" mb={6}>
        <Text size="xs" fw={600} c="dimmed" tt="uppercase">
          <Group gap={4} component="span">
            <LuSignature size={14} />
            {label}
          </Group>
        </Text>
        {!isEmpty && (
          <Button variant="subtle" size="xs" leftSection={<LuEraser size={14} />} onClick={clear}>
            Bersihkan
          </Button>
        )}
      </Group>
      <Box
        style={{
          border: '1px dashed var(--mantine-color-default-border)',
          borderRadius: 6,
          background: 'light-dark(white, var(--mantine-color-dark-7))',
          width,
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          style={{ display: 'block', cursor: 'crosshair', touchAction: 'none' }}
        />
      </Box>
      <Text size="xs" c="dimmed" mt={4}>
        Tarik mouse / sentuh layar untuk menggambar tanda tangan.
      </Text>
    </Box>
  )
}
